import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface CustomerFormProps {
  customer?: any;
  onBack: () => void;
  onSave: () => void;
}

export default function CustomerForm({ customer, onBack, onSave }: CustomerFormProps) {
  const [loading, setLoading] = useState(false);
  const [commercialsLoading, setCommercialsLoading] = useState(true);
  const [commercials, setCommercials] = useState<{ id: number; name: string }[]>([]);

  const [formData, setFormData] = useState({
    // Identification
    raison_sociale: customer?.raison_sociale || '',
    nom_commercial: customer?.nom_commercial || '',
    nif: customer?.nif || '',
    registre_commerce: customer?.registre_commerce || '',

    // Coordonnées
    adresse_facturation: customer?.adresse_facturation || '',
    quartier: customer?.quartier || '',
    ville: customer?.ville || 'Douala',
    pays: customer?.pays || 'Cameroun',

    // Contact
    contact: customer?.contact || '',
    fonction_contact: customer?.fonction_contact || '',
    telephone: customer?.telephone || '',
    telephone_2: customer?.telephone_2 || '',
    email: customer?.email || '',
    whatsapp: customer?.whatsapp || '',

    // Statut Commercial
    type_client: customer?.type_client || 'PROFESSIONNEL',
    delai_paiement_jours: customer?.delai_paiement_jours || 30,
    plafond_credit: customer?.plafond_credit || 0,
    risque_credit: customer?.risque_credit || 'MOYEN',
    commercial_id: customer?.commercial_id || null,
    actif: customer?.actif ?? true,
    bloque: customer?.bloque ?? false,
    motif_blocage: customer?.motif_blocage || '',

    // Adresses de livraison multiples (stockées localement pour l'UI)
    deliveryAddresses: customer?.deliveryAddresses || [],
  });

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: '',
    address: '',
    quartier: '',
    ville: 'Douala',
    pays: 'Cameroun',
    isDefault: false,
  });

  // Charger dynamiquement la liste des commerciaux
  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        setCommercialsLoading(true);
        const response = await getResources('devis-bcc/commerciaux'); // ← À adapter à ta route
        setCommercials(response.data.data || response.data); // Suppose que l'API renvoie { data: [...] }
      } catch (err) {
        toast.error('Erreur lors du chargement des commerciaux');
        console.error(err);
      } finally {
        setCommercialsLoading(false);
      }
    };

    fetchCommercials();
  }, []);

  const handleAddDeliveryAddress = () => {
    if (!newAddress.label || !newAddress.address) {
      toast.error('Libellé et adresse sont obligatoires');
      return;
    }

    setFormData({
      ...formData,
      deliveryAddresses: [...formData.deliveryAddresses, { ...newAddress, id: Date.now() }],
    });

    setNewAddress({
      label: '',
      address: '',
      quartier: '',
      ville: 'Douala',
      pays: 'Cameroun',
      isDefault: false,
    });
    setShowAddressModal(false);
  };

  const handleRemoveAddress = (addressId: number) => {
    setFormData({
      ...formData,
      deliveryAddresses: formData.deliveryAddresses.filter((addr: any) => addr.id !== addressId),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.raison_sociale.trim()) {
      toast.error('La raison sociale est obligatoire');
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Email invalide');
      return;
    }

    setLoading(true);

    const payload = {
      raison_sociale: formData.raison_sociale,
      nom_commercial: formData.nom_commercial || null,
      nif: formData.nif || null,
      registre_commerce: formData.registre_commerce || null,
      contact: formData.contact || null,
      fonction_contact: formData.fonction_contact || null,
      telephone: formData.telephone || null,
      telephone_2: formData.telephone_2 || null,
      email: formData.email || null,
      whatsapp: formData.whatsapp || null,
      adresse_facturation: formData.adresse_facturation || null,
      quartier: formData.quartier || null,
      ville: formData.ville,
      pays: formData.pays,
      type_client: formData.type_client,
      delai_paiement_jours: parseInt(String(formData.delai_paiement_jours)) || 30,
      plafond_credit: parseFloat(String(formData.plafond_credit)) || 0,
      risque_credit: formData.risque_credit,
      commercial_id: formData.commercial_id || null,
      actif: formData.actif,
      bloque: formData.bloque,
      motif_blocage: formData.bloque ? formData.motif_blocage : null,
      // encours_actuel et encours_retard sont calculés côté serveur → non envoyés
    };

    try {
      if (customer) {
        await updateResource(`clients`,customer.id, payload);
        toast.success('Client modifié avec succès !');
      } else {
        await createResource('clients', payload);
        toast.success('Client créé avec succès !');
      }
      onSave();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erreur lors de l\'enregistrement du client';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900">
          <i className="ri-arrow-left-line mr-2"></i>
          Retour à la liste
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Code Client (lecture seule en modification) */}
        {customer && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Code Client</label>
            <input
              type="text"
              value={customer.code || 'Auto-généré'}
              disabled
              className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg"
            />
          </div>
        )}

        {/* Identification & Administration */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i className="ri-building-line mr-2 text-blue-600"></i>
            Identification & Administration
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Raison Sociale *</label>
              <input
                type="text"
                value={formData.raison_sociale}
                onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom Commercial</label>
              <input
                type="text"
                value={formData.nom_commercial}
                onChange={(e) => setFormData({ ...formData, nom_commercial: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">NIF</label>
              <input
                type="text"
                value={formData.nif}
                onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Registre de Commerce</label>
              <input
                type="text"
                value={formData.registre_commerce}
                onChange={(e) => setFormData({ ...formData, registre_commerce: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Adresse Facturation */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i className="ri-map-pin-line mr-2 text-blue-600"></i>
            Adresse de Facturation
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
              <textarea
                value={formData.adresse_facturation}
                onChange={(e) => setFormData({ ...formData, adresse_facturation: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quartier</label>
              <input
                type="text"
                value={formData.quartier}
                onChange={(e) => setFormData({ ...formData, quartier: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
              <input
                type="text"
                value={formData.ville}
                onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
              <input
                type="text"
                value={formData.pays}
                onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i className="ri-contacts-line mr-2 text-blue-600"></i>
            Coordonnées de Contact
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Principal (Nom)</label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fonction</label>
              <input
                type="text"
                value={formData.fonction_contact}
                onChange={(e) => setFormData({ ...formData, fonction_contact: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone 2</label>
              <input
                type="tel"
                value={formData.telephone_2}
                onChange={(e) => setFormData({ ...formData, telephone_2: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
              <input
                type="text"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Conditions Commerciales & Crédit */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i className="ri-money-euro-circle-line mr-2 text-blue-600"></i>
            Conditions Commerciales & Crédit
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de Client *</label>
              <select
                value={formData.type_client}
                onChange={(e) => setFormData({ ...formData, type_client: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-8"
                required
              >
                <option value="PARTICULIER">Particulier</option>
                <option value="PROFESSIONNEL">Professionnel</option>
                <option value="GROSSISTE">Grossiste</option>
                <option value="SEMI_GROSSISTE">Semi-Grossiste</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Délai de Paiement (jours) *</label>
              <input
                type="number"
                min="0"
                value={formData.delai_paiement_jours}
                onChange={(e) => setFormData({ ...formData, delai_paiement_jours: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Plafond de Crédit (GNF) *</label>
              <input
                type="number"
                min="0"
                step="1000"
                value={formData.plafond_credit}
                onChange={(e) => setFormData({ ...formData, plafond_credit: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Risque Crédit</label>
              <select
                value={formData.risque_credit}
                onChange={(e) => setFormData({ ...formData, risque_credit: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-8"
              >
                <option value="FAIBLE">Faible</option>
                <option value="MOYEN">Moyen</option>
                <option value="ELEVE">Élevé</option>
                <option value="CRITIQUE">Critique</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commercial Responsable
                {commercialsLoading && <span className="text-gray-500 ml-2">(Chargement...)</span>}
              </label>
              <select
                value={formData.commercial_id}
                onChange={(e) => setFormData({ ...formData, commercial_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-8"
                disabled={commercialsLoading}
              >
                <option value="">Aucun commercial</option>
                {commercials.map((com) => (
                  <option key={com.id} value={com.id}>
                    {com.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Blocage */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.bloque}
                  onChange={(e) => setFormData({ ...formData, bloque: e.target.checked })}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Client Bloqué</span>
              </label>
            </div>

            {formData.bloque && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motif du Blocage</label>
                <textarea
                  value={formData.motif_blocage}
                  onChange={(e) => setFormData({ ...formData, motif_blocage: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-red-500 bg-red-50"
                  placeholder="Précisez la raison du blocage..."
                />
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <i className="ri-alert-line text-yellow-600 mt-0.5 mr-2"></i>
              <p className="text-sm text-yellow-800">
                <strong>Attention :</strong> La modification du plafond de crédit, du délai de paiement ou du statut de blocage nécessite une validation hiérarchique.
              </p>
            </div>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Enregistrement...' : customer ? 'Modifier le Client' : 'Créer le Client'}
          </button>
        </div>
      </form>
    </div>
  );
}